"use client";
import { Heading, Paragraph, SectionWithContainer } from "@/components";
import { NextIcon, PrevIcon } from "@/icons/icons";
import Image from "next/image";
import { useState } from "react";

interface IAmenities {
  title: string;
  description: string;
  items: {
    title: string;
    description: string;
    icon: string;
    src: string;
    color: string;
  }[];
}

const Amenities: React.FC<IAmenities> = ({ title, description, items }) => {
  const positions = [
    "top-1/2 left-0 -translate-x-1/2 -translate-y-1/2 transition-all duration-700 ease-linear scale-100",
    "top-[70%] lg:left-4 left-3 -translate-x-1/2 -translate-y-1/2 scale-[0.8] transition-all duration-700 ease-linear",
    "lg:top-[85%] top-[88%] lg:left-[4rem] left-[3rem] -translate-x-1/2 -translate-y-1/2 scale-[0.7] transition-all duration-700 ease-linear",
    "lg:top-[95%] top-[98%] left-[4rem] translate-x-1/2 -translate-y-1/2 scale-[0.6] transition-all duration-700 ease-linear",
    "lg:top-[6%] top-[0.74rem] left-[4.2rem] lg:right-[22.5rem]  translate-x-1/2 -translate-y-1/2 scale-[0.6] transition-all duration-700 ease-linear",
    "top-[15%] lg:right-[26rem] left-0 translate-x-1/2 -translate-y-1/2 scale-[0.7] transition-all duration-700 ease-linear",
    "lg:top-[30%] top-[30.5%] lg:-left-[3rem] -left-[2rem] translate-x-1/2 -translate-y-1/2 scale-[0.8] transition-all duration-700 ease-linear",
  ];

  const [currentIndex, setCurrentIndex] = useState(0);
  const [iconPositions, setIconPositions] = useState(
    items.map((_, index) => positions[index % positions.length])
  );
  const handleNext = () => {
    if (currentIndex === items.length - 1) {
      setCurrentIndex(0); // Reset to first item
      setIconPositions(
        items.map((_, index) => positions[index % positions.length])
      );
    } else if (currentIndex < items.length - 1) {
      const newPositions = [...iconPositions];
      newPositions.unshift(newPositions.pop()!); // Rotate positions forward
      setIconPositions(newPositions);
      setCurrentIndex((prev) => prev + 1);
    }
  };

  const handlePrev = () => {
    if (currentIndex === 0) {
      setCurrentIndex(items.length - 1); // Reset to last item
      const newPositions = [...iconPositions];
      newPositions.push(newPositions.shift()!); // Rotate positions backward
      setIconPositions(newPositions);
    }
    if (currentIndex > 0) {
      const newPositions = [...iconPositions];
      newPositions.push(newPositions.shift()!); // Rotate positions backward
      setIconPositions(newPositions);
      setCurrentIndex((prev) => prev - 1);
    }
  };

  return (
    <SectionWithContainer sectionClassName="bg-bgClr lg:pb-28">
      <div className="w-full flex flex-col items-center justify-center gap-4">
        <Heading
          level={2}
          className="text-center text-primary uppercase font-medium heading4"
        >
          {title}
        </Heading>
        <Paragraph className="text-center text-white heading3 font-montaga capitalize tracking-wider">
          {description}
        </Paragraph>
        <div className="w-full lg:grid grid-cols-2 gap-4 flex flex-col-reverse items-center mt-8">
          {/* Left Content */}
          <div className="w-full flex-col h-full justify-center flex gap-4">
            <div className="flex flex-col lg:gap-4 gap-2 min-h-[10rem]">
              <Heading
                level={2}
                className="text-white capitalize font-montaga text-2xl"
              >
                {items[currentIndex].title}
              </Heading>
              <Paragraph className="description1 text-white">
                {items[currentIndex].description}
              </Paragraph>
            </div>
            <div className="flex gap-1 items-center">
              <button
                onClick={handlePrev}
                className="text-white hover:scale-105 active:scale-95 w-8 aspect-square flex items-center justify-center"
              >
                <PrevIcon />
              </button>
              <button
                onClick={handleNext}
                className="text-white hover:scale-105 w-8 aspect-square active:scale-95 flex items-center justify-center"
              >
                <NextIcon />
              </button>
              <div className="text-white heading4 flex gap-1">
                <span className="w-3">{currentIndex + 1}</span>/
                <span className="w-3">{items.length}</span>
              </div>
            </div>
          </div>
          {/* Right Content */}
          <div className="w-full flex items-center justify-center overflow-hidden ps-16 py-6">
            {/* Main Image */}
            <div className="lg:p-11 p-9 relative flex items-center justify-center border-l-2 border-[#A0A0A0] rounded-full">
              <div className="w-full flex items-center bg-bgClr rounded-full p-1 z-10 justify-center overflow-hidden">
                <div className="relative flex items-center justify-center lg:w-[400px] lg:h-[400px] w-[240px] h-[240px] aspect-square rounded-full overflow-hidden z-20">
                  <Image
                    src={items[currentIndex]?.src}
                    alt={items[currentIndex]?.title}
                    fill
                    className="object-cover"
                    priority
                    loading="eager"
                  />
                </div>
              </div>
              {/* Rotating Icons */}
              {items?.map((item, index) => (
                <div
                  key={index}
                  className={`lg:w-16 w-11 border-2 border-white aspect-square absolute ${iconPositions[index]}  flex items-center justify-center rounded-full transition-all duration-700 ease-linear`}
                  style={{ backgroundColor: item.color }}
                >
                  <div className="relative lg:w-8 w-6 aspect-square">
                    <Image
                      src={item.icon}
                      alt={item.title}
                      fill
                      className="object-cover"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </SectionWithContainer>
  );
};

export default Amenities;
